import React, { useEffect, useState, useCallback } from "react";
import {
  FileClock, ChevronRight, Stethoscope, ClipboardCheck, CheckCircle2,
  Hash, Activity, CalendarClock, UserCircle2, Download, FileText, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/common/StatusBadge";
import CareRecord from "@/components/clinical/CareRecord";
import { useAuth } from "@/contexts/AuthContext";
import { api, API, getToken } from "@/lib/api";

const fmt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");
const fmtDate = (s) => (s ? String(s).slice(0, 10) : "—");

async function downloadCareRecordPdf(requestId, filename, scope = "episode") {
  const res = await fetch(`${API}/onboarding/requests/${requestId}/care-record/pdf?scope=${scope}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error("PDF export failed");
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

// Lightweight icon per timeline event type.
function eventIcon(ev) {
  if (!ev) return Activity;
  if (ev.includes("medical")) return Stethoscope;
  if (ev.includes("operational")) return ClipboardCheck;
  if (ev.includes("mrn")) return Hash;
  if (ev.includes("visit") || ev.includes("complete")) return CheckCircle2;
  return Activity;
}

export default function PatientEpisodes({ mrn, nationalId, patientName, dischargeDate, patientStatus }) {
  const { hasPrivilege } = useAuth();
  const canCareRecord = hasPrivilege?.("care_record.view");
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);     // episode being viewed
  const [timeline, setTimeline] = useState([]);
  const [tlLoading, setTlLoading] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownload = useCallback(async (ep, e) => {
    if (e) e.stopPropagation();
    setDownloadingId(ep.id);
    try {
      await downloadCareRecordPdf(ep.id, `care-record-${ep.mrn || ep.ref_number}.pdf`);
      toast.success("Care Record PDF downloaded");
    } catch {
      toast.error("Could not generate the PDF. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  }, []);

  useEffect(() => {
    const params = {};
    if (mrn) params.mrn = mrn;
    if (nationalId) params.national_id = nationalId;
    if (!params.mrn && !params.national_id) { setLoading(false); return; }
    api.get("/onboarding/patient-episodes", { params })
      .then((r) => setEpisodes(r.data.items))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [mrn, nationalId]);

  const openEpisode = useCallback(async (ep) => {
    setActive(ep);
    setTimeline([]);
    setShowFull(false);
    setTlLoading(true);
    try {
      const r = await api.get(`/onboarding/requests/${ep.id}/timeline`);
      setTimeline(r.data.items || []);
    } catch { /* ignore */ } finally { setTlLoading(false); }
  }, []);

  if (loading) return <div className="py-10 text-center text-sm text-[#6B7280]" data-testid="episodes-loading">Loading episodes…</div>;

  if (!episodes.length) {
    return (
      <div className="hah-card p-8 text-center" data-testid="episodes-empty">
        <FileClock className="w-8 h-8 mx-auto mb-2 text-[#CBD5E1]" />
        <div className="text-sm text-[#6B7280]">No treatment episodes found for this patient.</div>
        <div className="text-xs text-[#94A3B8] mt-1">Episodes appear once an onboarding request is linked to this MRN or National ID.</div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="patient-episodes">
      <div className="text-sm text-[#6B7280] mb-1">
        {episodes.length} treatment episode{episodes.length === 1 ? "" : "s"} — click an ID to view its full history{canCareRecord ? " or download the Care Record PDF" : ""}.
      </div>
      {episodes.map((ep) => (
        <div
          key={ep.id}
          role="button"
          tabIndex={0}
          onClick={() => openEpisode(ep)}
          onKeyDown={(e) => { if (e.key === "Enter") openEpisode(ep); }}
          data-testid={`episode-${ep.ref_number}`}
          className="w-full text-left hah-card p-4 flex items-center gap-4 hover:border-[#0B7E8C] hover:shadow-sm transition-all cursor-pointer"
        >
          <div className="w-10 h-10 rounded-lg bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center shrink-0">
            <FileClock className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-[#0B7E8C] font-mono">{ep.ref_number}</span>
              <StatusBadge status={ep.status} />
              {ep.disposition && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#64748B1A] text-[#475569]">{ep.disposition.label}</span>}
            </div>
            <div className="text-xs text-[#6B7280] mt-1 truncate">
              {ep.primary_diagnosis || "—"}{ep.requested_service ? ` · ${ep.requested_service}` : ""}
            </div>
            <div className="text-[11px] text-[#94A3B8] mt-1 flex items-center gap-3 flex-wrap">
              <span><CalendarClock className="w-3 h-3 inline mr-1" />Initiated {fmtDate(ep.created_at)}</span>
              {ep.operational_approved_at && <span>Approved {fmtDate(ep.operational_approved_at)}</span>}
              {ep.visit_completed_at && <span>Visit done {fmtDate(ep.visit_completed_at)}</span>}
            </div>
          </div>
          {canCareRecord && (
            <button
              type="button"
              onClick={(e) => handleDownload(ep, e)}
              disabled={downloadingId === ep.id}
              title="Download Care Record PDF"
              data-testid={`episode-pdf-${ep.ref_number}`}
              className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA] transition-colors disabled:opacity-50"
            >
              <Download className="w-3.5 h-3.5" /> {downloadingId === ep.id ? "…" : "PDF"}
            </button>
          )}
          <ChevronRight className="w-5 h-5 text-[#CBD5E1] shrink-0" />
        </div>
      ))}

      <Sheet open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-[820px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle className="flex items-center gap-2">
              <span className="font-mono text-[#0B7E8C]">{active?.ref_number}</span>
              {active && <StatusBadge status={active.status} />}
            </SheetTitle>
            <SheetDescription>
              {patientName} · {active?.primary_diagnosis || "—"} · Initiated {fmtDate(active?.created_at)}
            </SheetDescription>
            {canCareRecord && active && (
              <div className="flex flex-wrap items-center gap-2 pt-3">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]"
                  onClick={() => setShowFull((v) => !v)}
                  data-testid="episode-toggle-care-record"
                >
                  <FileText className="w-4 h-4 mr-1.5" />
                  {showFull ? "Hide full Care Record" : "View full Care Record"}
                  <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showFull ? "rotate-180" : ""}`} />
                </Button>
                <Button
                  size="sm"
                  className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
                  onClick={() => handleDownload(active)}
                  disabled={downloadingId === active.id}
                  data-testid="episode-panel-pdf"
                >
                  <Download className="w-4 h-4 mr-1.5" /> {downloadingId === active.id ? "Preparing…" : "Download PDF"}
                </Button>
              </div>
            )}
          </SheetHeader>
          <div className="p-6">
            {canCareRecord && showFull && active && (
              <div className="mb-6" data-testid="episode-care-record">
                <CareRecord requestId={active.id} refNumber={active.ref_number} />
                <div className="border-t border-[#E5E7EB] my-6" />
              </div>
            )}
            {(() => {
              const milestones = active ? [
                { label: "Onboarding initiated", at: active.created_at, icon: FileClock },
                { label: "Submitted for review", at: active.submitted_at, icon: Activity },
                { label: "Medically approved", at: active.medical_approved_at, icon: Stethoscope },
                { label: "Operationally approved", at: active.operational_approved_at, icon: ClipboardCheck },
                { label: active.mrn ? `MRN assigned (${active.mrn})` : "MRN assigned", at: active.mrn ? active.operational_approved_at : null, icon: Hash },
                { label: "Home visit completed", at: active.visit_completed_at, icon: CheckCircle2 },
                { label: "Discharged", at: patientStatus === "discharged" ? dischargeDate : null, icon: CheckCircle2 },
              ].filter((m) => m.at) : [];
              return (
                <>
                  <div className="text-[11px] uppercase tracking-wide text-[#94A3B8] font-semibold mb-4">Key milestones (onboarding → discharge)</div>
                  <div className="relative pl-2 mb-6" data-testid="episode-milestones">
                    {milestones.map((m, i) => {
                      const Icon = m.icon;
                      const isLast = i === milestones.length - 1;
                      return (
                        <div key={m.label} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            <div className="w-8 h-8 rounded-full bg-[#0B7E8C] text-white flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>
                            {!isLast && <div className="w-0.5 flex-1 bg-[#0B7E8C]/30 my-1" />}
                          </div>
                          <div className="pb-4 min-w-0">
                            <div className="text-sm font-semibold text-[#1F2937]">{m.label}</div>
                            <div className="text-[11px] text-[#94A3B8] mt-0.5">{fmt(m.at)}</div>
                          </div>
                        </div>
                      );
                    })}
                    {milestones.length === 0 && <div className="text-sm text-[#94A3B8] italic">No milestones recorded yet.</div>}
                  </div>
                </>
              );
            })()}
            <div className="text-[11px] uppercase tracking-wide text-[#94A3B8] font-semibold mb-4 border-t border-[#E5E7EB] pt-4">Detailed activity log</div>
            {tlLoading && <div className="text-sm text-[#6B7280]">Loading history…</div>}
            {!tlLoading && timeline.length === 0 && <div className="text-sm text-[#94A3B8] italic">No detailed activity events for this episode.</div>}
            <div className="relative pl-2">
              {timeline.map((t, i) => {
                const Icon = eventIcon(t.event);
                const isLast = i === timeline.length - 1;
                return (
                  <div key={t.id} className="flex gap-3" data-testid={`episode-event-${i}`}>
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-white border-2 border-[#0B7E8C] text-[#0B7E8C] flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4" />
                      </div>
                      {!isLast && <div className="w-0.5 flex-1 bg-[#E5E7EB] my-1" />}
                    </div>
                    <div className={`pb-5 min-w-0 ${isLast ? "" : ""}`}>
                      <div className="text-sm font-semibold text-[#1F2937]">{t.title}</div>
                      {t.description && <div className="text-xs text-[#6B7280] mt-0.5">{t.description}</div>}
                      <div className="text-[11px] text-[#94A3B8] mt-1 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1"><UserCircle2 className="w-3 h-3" />{t.actor_name || "System"}{t.actor_role ? ` (${t.actor_role})` : ""}</span>
                        <span>· {fmt(t.created_at)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
